import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, Phone } from 'lucide-react';

export const ARAB_COUNTRIES = [
  { name: 'اليمن', code: '+967', flag: '🇾🇪', id: 'YE' },
  { name: 'السعودية', code: '+966', flag: '🇸🇦', id: 'SA' },
  { name: 'الإمارات', code: '+971', flag: '🇦🇪', id: 'AE' },
  { name: 'مصر', code: '+20', flag: '🇪🇬', id: 'EG' },
  { name: 'الكويت', code: '+965', flag: '🇰🇼', id: 'KW' },
  { name: 'قطر', code: '+974', flag: '🇶🇦', id: 'QA' },
  { name: 'البحرين', code: '+973', flag: '🇧🇭', id: 'BH' },
  { name: 'عمان', code: '+968', flag: '🇴🇲', id: 'OM' },
  { name: 'الأردن', code: '+962', flag: '🇯🇴', id: 'JO' },
  { name: 'لبنان', code: '+961', flag: '🇱🇧', id: 'LB' },
  { name: 'سوريا', code: '+963', flag: '🇸🇾', id: 'SY' },
  { name: 'العراق', code: '+964', flag: '🇮🇶', id: 'IQ' },
  { name: 'فلسطين', code: '+970', flag: '🇵🇸', id: 'PS' },
  { name: 'المغرب', code: '+212', flag: '🇲🇦', id: 'MA' },
  { name: 'الجزائر', code: '+213', flag: '🇩🇿', id: 'DZ' },
  { name: 'تونس', code: '+216', flag: '🇹🇳', id: 'TN' },
  { name: 'ليبيا', code: '+218', flag: '🇱🇾', id: 'LY' },
  { name: 'السودان', code: '+249', flag: '🇸🇩', id: 'SD' },
  { name: 'موريتانيا', code: '+222', flag: '🇲🇷', id: 'MR' },
];

export default function PhoneInput({
  phoneValue,
  onPhoneChange,
  countryCodeValue,
  onCountryCodeChange,
  required = false,
  label = "رقم الجوال",
  containerClassName = "w-full",
  inputClassName = "w-full px-5 py-4 bg-transparent outline-none placeholder:text-slate-400 dark:placeholder:text-zinc-600 tracking-wider hover:bg-white/50 dark:hover:bg-white/5 transition-colors",
  bgClassName = "bg-white/50 dark:bg-black/20 hover:bg-white dark:hover:bg-white/5 shadow-sm"
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const pickerRef = useRef(null);

  // Close picker on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentCountry = ARAB_COUNTRIES.find(c => c.code === countryCodeValue) || ARAB_COUNTRIES[0];

  return (
    <div className={containerClassName}>
      <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
        <Phone className="w-4 h-4 text-slate-400" /> {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className={`flex border border-slate-200 dark:border-white/10 rounded-2xl focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white overflow-visible relative ${bgClassName}`}>
        <div className="relative shrink-0" ref={pickerRef}>
          <button
            type="button"
            onClick={() => setShowPicker(!showPicker)}
            className="flex items-center gap-2 h-full px-4 py-4 border-l border-slate-200 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/10 transition-colors outline-none"
          >
            <span className="text-2xl leading-none">{currentCountry.flag}</span>
            <span className="text-md tracking-widest font-mono" dir="ltr">{currentCountry.code}</span>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>

          {showPicker && (
            <div className="absolute top-full mt-2 right-0 w-72 bg-white/95 dark:bg-zinc-800/95 backdrop-blur-3xl border border-slate-200 dark:border-zinc-700 rounded-2xl shadow-2xl z-[100] overflow-hidden transform origin-top-right transition-all">
              <div className="p-3 border-b border-slate-100 dark:border-zinc-700/50">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="ابحث عن الدولة..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-3 pr-10 py-3 bg-white dark:bg-black/40 border border-slate-200 dark:border-white/5 rounded-xl outline-none focus:border-primary-500 text-md font-medium text-slate-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto p-2 scrollbar-thin">
                {ARAB_COUNTRIES.filter(c => c.name.includes(search) || c.code.includes(search)).length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-md font-medium">لم يتم العثور على نتائج</div>
                ) : (
                  ARAB_COUNTRIES.filter(c => c.name.includes(search) || c.code.includes(search)).map(country => (
                    <button
                      key={country.id}
                      type="button"
                      onClick={() => {
                        onCountryCodeChange(country.code);
                        setShowPicker(false);
                        setSearch('');
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors hover:bg-slate-50 dark:hover:bg-white/5 ${countryCodeValue === country.code ? 'bg-primary-50 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400' : 'text-slate-700 dark:text-zinc-300'}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl leading-none">{country.flag}</span>
                        <span className="font-bold text-md">{country.name}</span>
                      </div>
                      <span className="text-md font-mono font-bold opacity-70" dir="ltr">{country.code}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <input
          type="tel"
          required={required}
          dir="ltr"
          value={phoneValue}
          onChange={onPhoneChange}
          placeholder="7**********"
          className={inputClassName}
          style={{ textAlign: 'left' }}
        />
      </div>
    </div>
  );
}
